import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import EntityCombobox from '@/components/EntityCombobox';
import api from '@/lib/api';
import { Mail } from 'lucide-react';

/**
 * Searchable, required client link for the Case / Appointment forms. Selecting a
 * client reports both its id and the resolved record (so the parent can snapshot
 * the name). When linked, the resolved name + billing email are shown read-only
 * (edited only in Client Management).
 *
 * "New client" never opens a modal: it navigates to the full client-create page.
 * Pass `onCreateNew` so the host page can stash its in-progress draft first; if
 * omitted, this navigates on its own with a ?returnTo back to the current URL.
 *
 * onChange(clientId, client) — client is the resolved record (or null).
 */
const ClientCombobox = ({ value, onChange, onCreateNew, testId = 'client-combobox' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clients, setClients] = useState([]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch { /* non-fatal; parent surfaces load errors */ }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const selected = clients.find((c) => c.id === value) || null;
  const items = clients.map((c) => ({
    value: c.id,
    label: c.display_name || '(unnamed client)',
    sublabel: c.type,
  }));

  const handleCreate = () => {
    if (onCreateNew) {
      onCreateNew();
      return;
    }
    const returnTo = encodeURIComponent(location.pathname + location.search);
    navigate(`/clients/new?returnTo=${returnTo}`);
  };

  return (
    <div className="space-y-2">
      <EntityCombobox
        items={items}
        value={value || ''}
        onChange={(id) => onChange(id, clients.find((c) => c.id === id) || null)}
        placeholder="Select a client…"
        searchPlaceholder="Search clients…"
        emptyText="No clients found."
        createLabel="New client"
        onCreate={handleCreate}
        testId={testId}
      />
      {selected && (
        <div className="text-sm rounded-md border bg-muted/40 px-3 py-2" data-testid={`${testId}-details`}>
          <div className="font-medium">{selected.display_name}</div>
          <div className="text-muted-foreground flex items-center gap-1 capitalize">
            <span>{selected.type}</span>
            {selected.billing_email && (
              <>
                <span aria-hidden>·</span>
                <Mail size={12} /> <span className="lowercase">{selected.billing_email}</span>
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Details are managed in Client Management.</p>
        </div>
      )}
    </div>
  );
};

export default ClientCombobox;
